import { useState, useEffect, useCallback, useRef } from 'react';
import { socialPostingService } from '../services/socialPosting';
import type { ExportJob } from '../services/socialPosting';

interface UseExportPollingOptions {
  jobId: string | null;
  interval?: number;
  onComplete?: (job: ExportJob) => void;
  onFailed?: (job: ExportJob) => void;
}

interface UseExportPollingResult {
  job: ExportJob | null;
  isPolling: boolean;
  progress: number;
  error: string | null;
}

export function useExportPolling({
  jobId,
  interval = 2000,
  onComplete,
  onFailed,
}: UseExportPollingOptions): UseExportPollingResult {
  const [job, setJob] = useState<ExportJob | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);

  // Keep callback refs fresh without triggering effect re-runs
  onCompleteRef.current = onComplete;
  onFailedRef.current = onFailed;

  const poll = useCallback(async (id: string) => {
    try {
      const status = await socialPostingService.getExportJobStatus(id);
      setJob(status);
      setError(null);
      return status;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setIsPolling(false);
      setError(null);
      return;
    }

    setIsPolling(true);

    // Initial poll
    poll(jobId).then((result) => {
      if (result && (result.status === 'completed' || result.status === 'downloaded')) {
        setIsPolling(false);
        onCompleteRef.current?.(result);
        return;
      }
      if (result && result.status === 'failed') {
        setIsPolling(false);
        onFailedRef.current?.(result);
      }
    });

    const timer = setInterval(async () => {
      const result = await poll(jobId);
      if (!result) return;

      if (result.status === 'completed' || result.status === 'downloaded') {
        clearInterval(timer);
        setIsPolling(false);
        onCompleteRef.current?.(result);
      } else if (result.status === 'failed') {
        clearInterval(timer);
        setIsPolling(false);
        onFailedRef.current?.(result);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [jobId, interval, poll]);

  const progress = job && job.totalCount > 0
    ? Math.round(((job.completedCount + job.failedCount) / job.totalCount) * 100)
    : 0;

  return { job, isPolling, progress, error };
}
